import { useQuery } from '@apollo/client';
import { Hook, WalletConnectionContext } from 'common-client';
import SubgraphContext from 'contexts/SubgraphContext';
import { useContext, useMemo } from 'react';
import { Auction } from '../entities/Auction';
import { AuctionsMultiQueryResponse, AuctionsQueryResponse, getGetAuctionsQuery } from '../queries/auction';

interface queryParams {
  orderBy?: string;
  orderAscending?: boolean;
  tokenNameSubstring?: string;
  assets?: string[];
  accountAddress?: string;
  creators?: string[];
  factories?: string[];
  onlyActive?: boolean;
}

/**
 * Hook for fetching auctions
 */
export function useAuctions(
  queryParams?: queryParams,
  pollInterval?: number,
): Hook<Auction[]> {
  const { auction } = useContext(SubgraphContext);
  const { chain } = useContext(WalletConnectionContext);

  // Date is in seconds, but divide by 10,000, ceil it, and then multiply by 10 because
  //   this avoids multiple component updates in a short timeframe triggering multiple
  //   calls to this function that would have a different min date variable each time
  const minEndDate = 10 * Math.floor(Date.now() / 10000);

  const variables = {
    tokenNameSubstring: queryParams?.tokenNameSubstring || undefined,
    account: queryParams?.accountAddress?.toLowerCase() || undefined,
    assets: queryParams?.assets?.map((asset) => asset.toLowerCase()) || undefined,
    creators: queryParams?.creators?.map((creator) => creator.toLowerCase()) || undefined,
    factories: undefined,
    minEndDate: queryParams?.onlyActive ? minEndDate : undefined,
    orderBy: queryParams?.orderBy || undefined,
    orderDirection: queryParams?.orderAscending ? 'asc' : 'desc',
  };
  const query = getGetAuctionsQuery(
    !!queryParams?.tokenNameSubstring,
    !!queryParams?.accountAddress,
    !!queryParams?.assets,
    !!queryParams?.creators,
    !!queryParams?.factories,
    !!queryParams?.onlyActive,
  );

  const { loading, error, data } = useQuery<AuctionsQueryResponse & AuctionsMultiQueryResponse>(
    query,
    {
      variables,
      client: auction!!,
      pollInterval,
    },
  );

  return useMemo(() => {
    if (loading) return Hook.loading();
    if (error || !data) return Hook.error('Failed to fetch account');
    let auctions: Auction[] = [];
    if (data.auctions) {
      auctions = data.auctions.map((auctionData) => new Auction(auctionData, chain));
    }
    if (data.askMatches && data.bidMatches) {
      const handledAuctionSet = new Set();
      data.askMatches.forEach((auctionData) => {
        if (!handledAuctionSet.has(auctionData.id)) {
          handledAuctionSet.add(auctionData.id);
          auctions.push(new Auction(auctionData, chain));
        }
      });
      data.bidMatches.forEach((auctionData) => {
        if (!handledAuctionSet.has(auctionData.id)) {
          handledAuctionSet.add(auctionData.id);
          auctions.push(new Auction(auctionData, chain));
        }
      });
      Auction.sortAuctions(auctions, variables.orderDirection, queryParams?.orderBy);
    }
    return Hook.success(auctions);
  }, [data, error, loading]);
}
